<template src="./selectproduct.html"></template>

<script>
  import { slider, slideritem } from 'vue-concise-slider'
  import SubNavigationBar from '../../../common/SubNavigationBar.vue'
  import axios from 'axios'
  import { toTitleCase, titleCaseCatalogItem } from '../../../../helpers/titleCase'
  import { resolveItemImageUrl, withNormalizedImages, itemHasResolvableImage } from '../../../../helpers/itemImageUrl'
  import {
    createCustomerSuppliedProduct,
    isCustomerSuppliedProduct
  } from '../../../../helpers/customerSuppliedProduct'
  import customerSuppliedImage from '../../../../assets/img/customer-supplied-product.png'

  export default {
    components: {
      slider,
      slideritem,
      'app-sub-navigation-bar': SubNavigationBar
    },
    props: {
      parent_products: {
        type: Array
      },
      sub_category: {
        type: Object
      },
      /** When set, selected product is assigned back to this variable slot key. */
      slotType: {
        type: [String, Number],
        default: null
      },
      /**
       * Optional regex source strings; search results must match at least one
       * (case-insensitive). Suggested products from the task are always shown.
       */
      searchFilters: {
        type: Array,
        default: function () { return [] }
      }
    },
    data: function () {
      return {
        products: [],
        searchResults: [],
        searchText: '',
        loading: false,
        searchTimer: null,
        options: {
          currentPage: 0
        }
      }
    },
    computed: {
      customerSuppliedProduct: function () {
        return createCustomerSuppliedProduct({ imageUrl: customerSuppliedImage })
      },
      localSearchResults: function () {
        const text = (this.searchText || '').trim().toLowerCase()
        if (text.length < 3) return []
        return (this.products || []).filter((p) => {
          if (isCustomerSuppliedProduct(p)) return false
          const name = String((p && (p.description || p.name)) || '').toLowerCase()
          return name.indexOf(text) !== -1 && this.matchesSearchFilter(p)
        })
      },
      displayProducts: function () {
        let catalog = []
        if (this.searchText && this.searchText.trim().length >= 3) {
          // Keep matching local products visible even if API search misses them.
          const merged = []
          const seen = {}
          const keyOf = function (p) {
            return String((p && (p.id || p.uuid || p.primary_supplier_code || p.supplier_id || p.description || p.name)) || '')
          }
          ;(this.searchResults || []).forEach((p) => {
            const key = keyOf(p)
            if (!p || !key || seen[key] || isCustomerSuppliedProduct(p)) return
            seen[key] = true
            merged.push(p)
          })
          ;(this.localSearchResults || []).forEach((p) => {
            const key = keyOf(p)
            if (!p || !key || seen[key] || isCustomerSuppliedProduct(p)) return
            seen[key] = true
            merged.push(p)
          })
          catalog = merged
        } else {
          catalog = (this.products || []).filter((p) => !isCustomerSuppliedProduct(p))
        }
        // Always first: same Select UX, reusable across every category/search.
        return [this.customerSuppliedProduct].concat(catalog)
      }
    },
    methods: {
      isCustomerSuppliedProduct: isCustomerSuppliedProduct,
      formatPrice: function (value) {
        const n = parseFloat(value)
        if (!Number.isFinite(n)) return '0.00'
        return n.toFixed(2)
      },
      productDisplayName: function (product) {
        return toTitleCase((product && (product.description || product.name)) || '')
      },

      getImageUrl: function (input) {
        const base = (this.$store && this.$store.getters && this.$store.getters['getImageUrl']) || ''
        if (typeof input === 'string') {
          const s = input.trim()
          if (!s) return null
          if (s.startsWith('http://') || s.startsWith('https://')) return s
          // Webpack may emit root-relative asset urls (/static/...)
          if (s.startsWith('/')) return s
          return base + s
        }
        return resolveItemImageUrl(input, base, null)
      },

      getTypeLabel: function (typeId) {
        if (typeId === null || typeId === undefined || typeId === '') return 'N/A'
        const s = String(typeId).trim()
        const maybeNumber = Number(s)
        // If it's already a human label, return it.
        if (!Number.isFinite(maybeNumber)) return s

        const types = this.$store.getters['material/getTypes'] || []
        const match = types.find((t) => {
          if (!t) return false
          return String(t.id) === s || String(t.order_id) === s
        })
        if (match && match.name) return match.name
        return s
      },

      hydrateProductImages: function (list) {
        const self = this
        const base = (this.$store && this.$store.getters && this.$store.getters['getImageUrl']) || ''
        ;(list || []).forEach(function (product) {
          if (!product || !product.id) return
          const normalized = withNormalizedImages(product)
          if (normalized !== product && normalized.images != null) {
            product.images = normalized.images
          }
          if (itemHasResolvableImage(product, base)) return
          if (product._hydratingImages) return
          product._hydratingImages = true
          axios.get('/product/' + product.id).then(function (response) {
            const payload = response && response.data
            const full = payload && payload.product ? payload.product : payload
            if (full && full.images != null) {
              product.images = withNormalizedImages({ images: full.images }).images
            }
          }).catch(function () { /* keep placeholder */ }).finally(function () {
            product._hydratingImages = false
          })
        })
      },
      getSupplierName: function (id) {
        if (id === null || id === undefined) return 'N/A'
        const suppliers = this.$store.getters['product/getSuppliers'] || []
        if (!Array.isArray(suppliers) || suppliers.length === 0) return 'N/A'
        const match = suppliers.find((s) => s && s.id === id)
        return match && match.name ? match.name : 'N/A'
      },
      selectProduct: function (product) {
        const payload = titleCaseCatalogItem(Object.assign({}, product))
        if (payload.primary_supplier_code == null && payload.supplier_id != null) {
          payload.primary_supplier_code = payload.supplier_id
        }
        if (this.slotType != null && this.slotType !== '') {
          payload.slot_type = this.slotType
        }
        this.$emit('select', payload)
      },
      matchesSearchFilter: function (product) {
        const filters = this.searchFilters || []
        if (!filters.length) return true
        const text = String((product && (product.description || product.name)) || '')
        return filters.some(function (pattern) {
          try {
            return new RegExp(pattern, 'i').test(text)
          } catch (e) {
            return text.toLowerCase().indexOf(String(pattern).toLowerCase()) !== -1
          }
        })
      },
      onSearchInput: function (value) {
        this.searchText = value == null ? '' : String(value)
        if (this.searchTimer) clearTimeout(this.searchTimer)
        if (this.searchText.trim().length < 3) {
          this.searchResults = []
          this.loading = false
          return
        }
        const self = this
        this.searchTimer = setTimeout(function () {
          self.runSearch()
        }, 250)
      },
      runSearch: function () {
        const text = (this.searchText || '').trim()
        if (text.length < 3) return
        const self = this
        this.loading = true
        Promise.all([
          axios.post('/product/fetch/search?type=default', { text: text }),
          axios.post('/product/fetch/search?type=company', { text: text })
        ]).then(function (responses) {
          const merged = []
          responses.forEach(function (response) {
            const data = (response && response.data) || {}
            ;(((data.default) || [])).forEach(function (p) { merged.push(p) })
            ;(((data.company) || [])).forEach(function (p) { merged.push(p) })
            ;(((data.products) || [])).forEach(function (p) { merged.push(p) })
          })
          const seen = {}
          self.searchResults = merged.filter(function (p) {
            if (!p || p.id == null) return false
            if (seen[p.id]) return false
            seen[p.id] = true
            if (p.primary_supplier_code == null && p.supplier_id != null) {
              p.primary_supplier_code = p.supplier_id
            }
            return self.matchesSearchFilter(p)
          }).map(withNormalizedImages)
          self.hydrateProductImages(self.searchResults)
        }).catch(function () {
          self.searchResults = []
        }).finally(function () {
          self.loading = false
        })
      }
    },
    mounted: function () {
      this.products = JSON.parse(JSON.stringify(this.parent_products || [])).map(withNormalizedImages)
      this.hydrateProductImages(this.products)
    },
    watch: {
      parent_products: {
        deep: true,
        handler: function (next) {
          this.products = JSON.parse(JSON.stringify(next || [])).map(withNormalizedImages)
          this.hydrateProductImages(this.products)
        }
      }
    },
    beforeUnmount: function () {
      if (this.searchTimer) clearTimeout(this.searchTimer)
    }
  }
</script>

<style scoped>
.task-modal-product-search {
  margin: 24px 0 12px;
  max-width: 100%;
  min-width: 0;
}
.task-modal-product-hint {
  margin: 0 0 16px;
  color: #666;
  max-width: 100%;
  overflow-wrap: anywhere;
}
</style>
